import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { API_BASE, getUserErrorMessage, parseJsonResponse } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';

const TYPE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'sale', label: 'Sale' },
  { id: 'return', label: 'Return' },
  { id: 'exchange_in', label: 'Exchange in' },
  { id: 'exchange_out', label: 'Exchange out' },
  { id: 'damage', label: 'Damage' },
  { id: 'tester', label: 'Used for testing' },
  { id: 'gift', label: 'Gift' },
];

const SUMMARY_CARDS = [
  { id: 'all', label: 'All movements', hint: 'Every stock change', key: 'all' },
  { id: 'sale', label: 'Sale', hint: 'Units sold', key: 'sale' },
  { id: 'return', label: 'Return', hint: 'Units returned', key: 'return' },
  { id: 'exchange', label: 'Exchange', hint: 'In + out', key: 'exchange' },
  { id: 'damage', label: 'Damage', hint: 'Written off', key: 'damage' },
  { id: 'tester', label: 'Used for testing', hint: 'Sample / demo', key: 'tester' },
  { id: 'gift', label: 'Gift', hint: 'Giveaways', key: 'gift' },
];

const EMPTY_SUMMARY = {
  all: { quantity: 0, entries: 0 },
  sale: { quantity: 0, entries: 0 },
  return: { quantity: 0, entries: 0 },
  exchange: { quantity: 0, entries: 0, in: 0, out: 0 },
  exchange_in: { quantity: 0, entries: 0 },
  exchange_out: { quantity: 0, entries: 0 },
  damage: { quantity: 0, entries: 0 },
  tester: { quantity: 0, entries: 0 },
  gift: { quantity: 0, entries: 0 },
};

function LedgerStatCard({ label, value, hint, active, onClick }) {
  return (
    <button
      type="button"
      className={`sp-ledger-stat-card${active ? ' sp-ledger-stat-card--active' : ''}`}
      onClick={onClick}
    >
      <span className="sp-stat-label">{label}</span>
      <strong className="sp-stat-value sp-stat-gold">{value}</strong>
      {hint ? <span className="sp-stat-hint">{hint}</span> : null}
    </button>
  );
}

export default function ShopStockLedgerPanel({ shopAccountId }) {
  const [type, setType] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);

  const dateLabel = useMemo(() => {
    if (!fromDate && !toDate) return 'Overall (all time)';
    if (fromDate && toDate) return `${fromDate} → ${toDate}`;
    if (fromDate) return `From ${fromDate}`;
    return `Until ${toDate}`;
  }, [fromDate, toDate]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ limit: '200' });
        if (type !== 'all') qs.set('type', type);
        if (fromDate) qs.set('from', fromDate);
        if (toDate) qs.set('to', toDate);

        const response = await authFetch(
          `${API_BASE}/v1/shop/accounts/${shopAccountId}/stock-ledger?${qs.toString()}`
        );
        const result = await parseJsonResponse(response);
        if (!cancelled) {
          setEntries(result.entries || []);
          setSummary({ ...EMPTY_SUMMARY, ...(result.summary || {}) });
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(getUserErrorMessage(error, 'Unable to load stock ledger.'));
          setEntries([]);
          setSummary(EMPTY_SUMMARY);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [shopAccountId, type, fromDate, toDate]);

  const clearDates = () => {
    setFromDate('');
    setToDate('');
  };

  const pickCard = (card) => {
    if (card.id === 'exchange') {
      // Combined exchange — use chips for in/out detail
      setType('all');
      return;
    }
    setType(card.id);
  };

  const cardActive = (card) => {
    if (card.id === 'exchange') return false;
    return type === card.id;
  };

  return (
    <div className="sp-ledger-panel">
      <div className="sp-detail-head">
        <div>
          <h3 className="sp-section-title sp-section-title--flush">Stock Ledger</h3>
          <p className="sp-section-desc">
            Audit trail of sales, returns, exchanges, damage, testing, and gifts. Default shows overall.
          </p>
        </div>
      </div>

      <div className="sp-ledger-date-filters">
        <div className="sp-ledger-date-field">
          <label htmlFor="ledger-from">From</label>
          <input
            id="ledger-from"
            type="date"
            className="sp-form-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="sp-ledger-date-field">
          <label htmlFor="ledger-to">To</label>
          <input
            id="ledger-to"
            type="date"
            className="sp-form-input"
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="sp-btn sp-btn--ghost"
          onClick={clearDates}
          disabled={!fromDate && !toDate}
        >
          Overall
        </button>
        <span className="sp-ledger-date-hint">{dateLabel}</span>
      </div>

      <div className="sp-ledger-stats-grid">
        {SUMMARY_CARDS.map((card) => {
          const stats = summary[card.key] || { quantity: 0, entries: 0 };
          let hint = `${stats.entries || 0} entries · ${card.hint}`;
          if (card.id === 'exchange') {
            hint = `In ${summary.exchange?.in || 0} · Out ${summary.exchange?.out || 0}`;
          }
          return (
            <LedgerStatCard
              key={card.id}
              label={card.label}
              value={stats.quantity || 0}
              hint={hint}
              active={cardActive(card)}
              onClick={() => pickCard(card)}
            />
          );
        })}
      </div>

      <div className="sp-filter-row">
        {TYPE_FILTERS.map((chip) => (
          <button
            key={chip.id}
            type="button"
            className={`sp-filter-chip ${type === chip.id ? 'sp-filter-chip--active' : ''}`}
            onClick={() => setType(chip.id)}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="sp-muted">Loading ledger…</p>
      ) : entries.length === 0 ? (
        <p className="sp-muted sp-muted--compact">No ledger entries for this filter.</p>
      ) : (
        <div className="sp-table-wrap">
          <table className="sp-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Product</th>
                <th>Type</th>
                <th>Qty</th>
                <th>Stock</th>
                <th>Invoice</th>
                <th>Photo</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry._id}>
                  <td>{new Date(entry.createdAt).toLocaleString()}</td>
                  <td>
                    <strong>{entry.productName}</strong>
                    <div className="sp-muted" style={{ fontSize: 11 }}>
                      {entry.barcode}
                    </div>
                  </td>
                  <td>
                    <span className="sp-inv-badge sp-inv-badge--in">{entry.type}</span>
                  </td>
                  <td>
                    <strong style={{ color: entry.direction === 'in' ? '#4ade80' : '#fca5a5' }}>
                      {entry.direction === 'in' ? '+' : '−'}
                      {entry.quantity}
                    </strong>
                  </td>
                  <td>
                    {entry.stockBefore} → {entry.stockAfter}
                  </td>
                  <td>{entry.invoiceNumber ? `#${entry.invoiceNumber}` : '—'}</td>
                  <td>
                    {entry.imageUrl ? (
                      <a href={entry.imageUrl} target="_blank" rel="noopener noreferrer">
                        View
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {entry.createdBy?.name || '—'}
                    <div className="sp-muted" style={{ fontSize: 11 }}>
                      {entry.createdBy?.role || ''}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
